import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { ResultStat, budgetBarColor, formatUsd } from './AdminConfigPage';
import {
  getOpenAiUsage,
  setOpenAiMonthlyBudget,
  type OpenAiCostView,
  type OpenAiUsageResult,
  type OpenAiVolumeView,
} from '../../services/firebase';
import { AI_CALL_TYPES, type AiCallType } from '../../services/aiRateLimitConfig';

/**
 * Configuration → OpenAI API: the second half of the AI bill.
 *
 * The Anthropic panel next door answers "how much is Claude costing us". This
 * one answers that for OpenAI and then answers a question neither panel could
 * before: *what were people doing* to run it up. Two views, deliberately stacked
 * rather than tabbed, because the interesting reading is the comparison — a day
 * where spend spikes and call volume doesn't is a different problem from a day
 * where both do.
 *
 * View 1 (spend) needs the `OPENAI_ADMIN_KEY` secret and shows an instruction
 * card until it exists. View 2 (volume) reads the `ai_usage_events` audit trail
 * and works today, which is why the callable degrades each half separately
 * instead of failing the whole call.
 *
 * ---- No charting library ---------------------------------------------------
 *
 * Both charts are flexbox and background colours. The repo has no chart
 * dependency and two bar charts is a thin reason to add one; the bars are the
 * same idiom as the budget progress bar the Anthropic panel already draws. Every
 * colour class is written out in full below because Tailwind scans source text —
 * a composed `bg-${x}-500` would not survive the build.
 */

/** Bar height for both charts. Deep enough that a quiet day is visibly not zero. */
const CHART_HEIGHT_PX = 160;

/** Human labels for the audit trail's call types. */
const CALL_TYPE_LABELS: Record<AiCallType, string> = {
  identify: 'Identify',
  smartSearch: 'Smart search',
  referenceLookup: 'Reference lookup',
  webSearch: 'Web search',
  imageWebSearch: 'Image web search',
  enrichment: 'Enrichment',
};

/** Static class strings, one per call type — see the Tailwind note above. */
const CALL_TYPE_COLORS: Record<AiCallType, string> = {
  identify: 'bg-indigo-500',
  smartSearch: 'bg-sky-500',
  referenceLookup: 'bg-emerald-500',
  webSearch: 'bg-amber-500',
  imageWebSearch: 'bg-rose-500',
  enrichment: 'bg-violet-500',
};

/** `2026-08-17` → `17 Aug`, for axis ticks. Parsed as UTC to match the keys. */
function shortDate(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function formatInt(n: number): string {
  return n.toLocaleString();
}

/**
 * Axis ticks under a chart. Only some columns get a label — 31 of them side by
 * side is unreadable — so this decides which, keeping the first and last.
 */
function ChartAxis({ dates }: { dates: string[] }) {
  const step = Math.max(1, Math.ceil(dates.length / 6));
  return (
    <div className="flex gap-px mt-1">
      {dates.map((date, i) => {
        const show = i === 0 || i === dates.length - 1 || i % step === 0;
        // min-w-0 stops a wide label widening its own column and shearing the
        // bars above out of alignment; the blank columns either side give it
        // room to overflow into, so it reads without being clipped.
        return (
          <div
            key={date}
            className="flex-1 min-w-0 overflow-visible whitespace-nowrap text-[10px] text-gray-400 text-center"
          >
            {show ? shortDate(date) : ' '}
          </div>
        );
      })}
    </div>
  );
}

/** Daily OpenAI spend. Bars scale to the window's peak, not to the budget — the
 * shape of the spend is the point, and a budget-scaled axis flattens it. */
function CostChart({ days, currency }: { days: OpenAiCostView['days']; currency: string }) {
  const peak = days.reduce((max, d) => Math.max(max, d.amountUsd), 0);

  if (!days.length) return null;

  return (
    <div>
      <div
        className="flex items-end gap-px"
        style={{ height: `${CHART_HEIGHT_PX}px` }}
      >
        {days.map((d) => {
          // A day with real but tiny spend still gets a sliver, so "a little" and
          // "nothing" don't both render as an empty column.
          const pct = peak > 0 ? (d.amountUsd / peak) * 100 : 0;
          const height = d.amountUsd > 0 ? Math.max(2, pct) : 0;
          return (
            <div
              key={d.date}
              className="flex-1 h-full flex items-end"
              title={`${shortDate(d.date)} — ${formatUsd(d.amountUsd, currency)}`}
            >
              <div
                className="w-full bg-emerald-500 rounded-t-sm"
                style={{ height: `${height}%` }}
              />
            </div>
          );
        })}
      </div>
      <ChartAxis dates={days.map((d) => d.date)} />
    </div>
  );
}

/** Daily call volume, stacked by call type. */
function VolumeChart({ days }: { days: OpenAiVolumeView['days'] }) {
  const peak = days.reduce((max, d) => Math.max(max, d.total), 0);

  if (!days.length) return null;

  return (
    <div>
      <div
        className="flex items-end gap-px"
        style={{ height: `${CHART_HEIGHT_PX}px` }}
      >
        {days.map((d) => {
          const columnPct = peak > 0 ? (d.total / peak) * 100 : 0;
          const breakdown = AI_CALL_TYPES.map(
            (t) => `${CALL_TYPE_LABELS[t]}: ${formatInt(d.byCallType?.[t] ?? 0)}`,
          ).join('\n');
          return (
            <div
              key={d.date}
              className="flex-1 h-full flex items-end"
              title={`${shortDate(d.date)} — ${formatInt(d.total)} calls\n${breakdown}`}
            >
              <div
                className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden"
                style={{ height: `${d.total > 0 ? Math.max(2, columnPct) : 0}%` }}
              >
                {AI_CALL_TYPES.map((t) => {
                  const n = d.byCallType?.[t] ?? 0;
                  if (n <= 0) return null;
                  return (
                    <div
                      key={t}
                      className={CALL_TYPE_COLORS[t]}
                      style={{ height: `${(n / d.total) * 100}%` }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <ChartAxis dates={days.map((d) => d.date)} />
    </div>
  );
}

function CallTypeLegend({ byCallType }: { byCallType: Record<string, number> }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {AI_CALL_TYPES.map((t) => (
        <div key={t} className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className={`inline-block w-2.5 h-2.5 rounded-sm ${CALL_TYPE_COLORS[t]}`} />
          {CALL_TYPE_LABELS[t]}
          <span className="text-gray-400">{formatInt(byCallType?.[t] ?? 0)}</span>
        </div>
      ))}
    </div>
  );
}

/** The spend half. */
function CostSection({
  costs,
  budgetInput,
  onBudgetInput,
  onSaveBudget,
  savingBudget,
}: {
  costs: OpenAiCostView;
  budgetInput: string;
  onBudgetInput: (v: string) => void;
  onSaveBudget: () => void;
  savingBudget: boolean;
}) {
  const currency = costs.currency || 'USD';
  const missingKey = costs.error?.code === 'failed-precondition';
  const target = costs.monthlyBudgetTargetUsd;
  const percent = costs.budgetUsedPercent;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">OpenAI spend</h4>
        <p className="mt-0.5 text-xs text-gray-500">
          What OpenAI billed the organisation, per UTC day. This is every OpenAI
          call across the whole account — the AI features and anything else on the
          same org.
        </p>
      </div>

      {costs.error && (
        <div
          className={`rounded-lg border p-4 ${
            missingKey ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Badge variant={missingKey ? 'amber' : 'danger'}>
              {missingKey ? 'admin key needed' : 'error'}
            </Badge>
            <span
              className={`text-sm font-semibold ${
                missingKey ? 'text-amber-800' : 'text-red-800'
              }`}
            >
              {missingKey
                ? 'Spend tracking is not switched on yet'
                : "Couldn't load OpenAI spend"}
            </span>
          </div>
          <p
            className={`text-sm whitespace-pre-line ${
              missingKey ? 'text-amber-800' : 'text-red-700'
            }`}
          >
            {costs.error.message}
          </p>
          {missingKey && (
            <p className="mt-2 text-xs text-amber-700">
              Call volume below is read from the audit trail and works without the
              key — only the dollar figures need it.
            </p>
          )}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <div>
          <h5 className="text-sm font-semibold text-gray-900">
            Monthly budget target
          </h5>
          <p className="mt-0.5 text-xs text-gray-500">
            A tracking target, not an alert — set spend alerts in the OpenAI
            dashboard, which is the only thing that can act on them. Leave empty to
            disable.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1" htmlFor="openai-budget">
              Target ($/month)
            </label>
            <div className="flex items-center">
              <span className="text-gray-500 mr-1">$</span>
              <input
                id="openai-budget"
                type="number"
                min="0"
                step="10"
                inputMode="decimal"
                className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="e.g. 200"
                value={budgetInput}
                onChange={(e) => onBudgetInput(e.target.value)}
                disabled={savingBudget}
              />
            </div>
          </div>
          <Button
            variant="secondary"
            loading={savingBudget}
            disabled={savingBudget}
            onClick={onSaveBudget}
          >
            Save
          </Button>
        </div>

        {target !== null && (
          <div className="space-y-1">
            <div className="text-sm text-gray-700">
              {costs.totalUsd !== null
                ? `${formatUsd(costs.totalUsd, currency)} of ${formatUsd(target, currency)} used over the window`
                : `Target ${formatUsd(target, currency)} / month`}
              {percent !== null && (
                <span
                  className={`ml-1 font-semibold ${
                    percent > 90
                      ? 'text-red-600'
                      : percent >= 75
                        ? 'text-amber-600'
                        : 'text-green-600'
                  }`}
                >
                  ({Math.round(percent)}%)
                </span>
              )}
            </div>
            {percent !== null && (
              <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                <div
                  className={`h-full ${budgetBarColor(percent)}`}
                  style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
                />
              </div>
            )}
            {costs.budgetRemainingUsd !== null && (
              <div className="text-xs text-gray-500">
                {formatUsd(costs.budgetRemainingUsd, currency)} remaining
                {percent !== null && percent > 90 && (
                  <span className="ml-1 text-red-600 font-medium">
                    — over 90% of target
                  </span>
                )}
                {percent !== null && percent >= 75 && percent <= 90 && (
                  <span className="ml-1 text-amber-600 font-medium">
                    — over 75% of target
                  </span>
                )}
              </div>
            )}
            {percent === null && (
              <div className="text-xs text-gray-400">
                No spend figure to compare against the target yet.
              </div>
            )}
          </div>
        )}
      </div>

      {costs.available && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <ResultStat
              label="Spend (window)"
              value={
                costs.totalUsd !== null ? formatUsd(costs.totalUsd, currency) : 'No data'
              }
            />
            <ResultStat
              label="Est. daily burn"
              value={
                costs.dailyBurnUsd !== null
                  ? formatUsd(costs.dailyBurnUsd, currency)
                  : 'No data'
              }
            />
            <ResultStat
              label="Peak day"
              value={
                costs.peakDayUsd !== null
                  ? formatUsd(costs.peakDayUsd, currency)
                  : 'No data'
              }
            />
            <ResultStat label="Current balance" value="Not exposed" />
          </div>

          <CostChart days={costs.days} currency={currency} />

          {costs.tokens && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <ResultStat
                label="Input tokens"
                value={formatInt(costs.tokens.inputTokens)}
              />
              <ResultStat
                label="Cached input"
                value={formatInt(costs.tokens.cachedInputTokens)}
              />
              <ResultStat
                label="Output tokens"
                value={formatInt(costs.tokens.outputTokens)}
              />
              <ResultStat
                label="Model requests"
                value={formatInt(costs.tokens.modelRequests)}
              />
            </div>
          )}

          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              OpenAI publishes no remaining-credit-balance endpoint — only usage
              and cost reports, same as Anthropic. For the raw balance, and to set
              spend alerts, use{' '}
              <a
                href="https://platform.openai.com/usage"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                the OpenAI usage dashboard
              </a>
              .
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** The audit-trail half. */
function VolumeSection({ volume }: { volume: OpenAiVolumeView }) {
  const busiest = useMemo(() => {
    let best: { date: string; total: number } | null = null;
    for (const d of volume.days) {
      if (!best || d.total > best.total) best = { date: d.date, total: d.total };
    }
    return best && best.total > 0 ? best : null;
  }, [volume.days]);

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">
          Call volume (audit trail)
        </h4>
        <p className="mt-0.5 text-xs text-gray-500">
          One row per AI call from{' '}
          <code className="bg-gray-100 px-1 py-0.5 rounded">ai_usage_events</code>,
          written server-side on every call — allowed or capped. These are the app&apos;s
          AI features only, so they explain part of the spend above rather than
          all of it.
        </p>
      </div>

      {volume.error && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="danger">error</Badge>
            <span className="text-sm font-semibold text-red-800">
              Couldn&apos;t load the audit trail
            </span>
          </div>
          <p className="text-sm text-red-700">{volume.error.message}</p>
        </div>
      )}

      {volume.truncated && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            Scan stopped at {formatInt(volume.scanCap)} events — every figure below
            is a floor, not a total, and the oldest days in the window are the ones
            missing. Narrow the window or roll the trail up daily if this becomes
            the normal case.
          </p>
        </div>
      )}

      {volume.unknownCallTypes > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            {formatInt(volume.unknownCallTypes)} events carry a call type this admin
            build doesn&apos;t know. watchlock has added one — the totals are right,
            the per-type breakdown is under-counting until the list here is updated.
          </p>
        </div>
      )}

      {volume.available && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ResultStat label="AI calls (window)" value={formatInt(volume.totalCalls)} />
            <ResultStat label="Distinct users" value={formatInt(volume.distinctUsers)} />
            <ResultStat
              label="Busiest day"
              value={
                busiest ? `${shortDate(busiest.date)} — ${formatInt(busiest.total)}` : 'No calls'
              }
            />
          </div>

          <VolumeChart days={volume.days} />
          <CallTypeLegend byCallType={volume.byCallType} />

          {volume.totalCalls > 0 && (
            <details className="border border-gray-200 rounded-lg">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-700">
                Per-day totals
              </summary>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">Day (UTC)</th>
                      <th className="px-3 py-1.5 text-right font-medium">Total</th>
                      {AI_CALL_TYPES.map((t) => (
                        <th key={t} className="px-3 py-1.5 text-right font-medium">
                          {CALL_TYPE_LABELS[t]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[...volume.days].reverse().map((d) => (
                      <tr key={d.date} className={d.total === 0 ? 'text-gray-400' : ''}>
                        <td className="px-3 py-1.5 whitespace-nowrap">{d.date}</td>
                        <td className="px-3 py-1.5 text-right font-medium">
                          {formatInt(d.total)}
                        </td>
                        {AI_CALL_TYPES.map((t) => (
                          <td key={t} className="px-3 py-1.5 text-right">
                            {formatInt(d.byCallType?.[t] ?? 0)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <div>
            <h5 className="text-sm font-semibold text-gray-900 mb-2">
              Top users over the window
            </h5>
            {volume.topUsers.length === 0 ? (
              <p className="text-sm text-gray-500">No AI calls in the window.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">User</th>
                      <th className="px-3 py-1.5 text-left font-medium">UID</th>
                      <th className="px-3 py-1.5 text-right font-medium">Calls</th>
                      <th className="px-3 py-1.5 text-right font-medium">Per day</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {volume.topUsers.map((u) => (
                      <tr key={u.userId}>
                        <td className="px-3 py-1.5">
                          {u.label ?? (
                            <span className="text-gray-400 italic">
                              no user document
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs text-gray-500">
                          {u.userId}
                        </td>
                        <td className="px-3 py-1.5 text-right font-medium">
                          {formatInt(u.count)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-500">
                          {volume.days.length
                            ? (u.count / volume.days.length).toFixed(1)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {Object.keys(volume.byTier).length > 0 && (
            <div className="text-xs text-gray-500">
              By tier:{' '}
              {Object.entries(volume.byTier)
                .sort((a, b) => b[1] - a[1])
                .map(([tier, n]) => `${tier} ${formatInt(n)}`)
                .join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OpenAiUsageSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OpenAiUsageResult | null>(null);
  const [budgetInput, setBudgetInput] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  const load = async (force: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const r = await getOpenAiUsage({ force });
      setData(r);
      setBudgetInput(
        r.costs.monthlyBudgetTargetUsd !== null
          ? String(r.costs.monthlyBudgetTargetUsd)
          : '',
      );
    } catch (err) {
      console.error('getOpenAiUsage failed:', err);
      setData(null);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch OpenAI usage — see console.',
      );
    } finally {
      setLoading(false);
    }
  };

  const saveBudget = async () => {
    const trimmed = budgetInput.trim();
    // Empty clears the target; otherwise require a positive finite number.
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      toast.error('Enter a positive dollar amount, or clear the field to unset.');
      return;
    }
    setSavingBudget(true);
    try {
      await setOpenAiMonthlyBudget(parsed);
      toast.success(parsed === null ? 'Budget target cleared.' : 'Budget target saved.');
      // Force past the cache — the snapshot it holds has the old target baked in.
      await load(true);
    } catch (err) {
      console.error('setOpenAiMonthlyBudget failed:', err);
      toast.error('Failed to save budget — see console.');
    } finally {
      setSavingBudget(false);
    }
  };

  // Auto-load once on mount, cache allowed, so the panel isn't blank on open.
  useEffect(() => {
    void load(false);
  }, []);

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            OpenAI API
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Recent OpenAI spend, and what users did to run it up. Spend comes from
            OpenAI&apos;s Usage API; call volume from the server-side audit trail.{' '}
            <a
              href="https://platform.openai.com/usage"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium"
            >
              Full dashboard
            </a>
          </p>
        </div>
        <Button
          variant="secondary"
          loading={loading}
          disabled={loading}
          onClick={() => void load(true)}
        >
          Refresh
        </Button>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="danger">error</Badge>
            <span className="text-sm font-semibold text-red-800">
              Couldn&apos;t load usage
            </span>
          </div>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white text-sm text-gray-500">
          Loading usage…
        </div>
      )}

      {data && (
        <>
          <CostSection
            costs={data.costs}
            budgetInput={budgetInput}
            onBudgetInput={setBudgetInput}
            onSaveBudget={() => void saveBudget()}
            savingBudget={savingBudget}
          />

          <VolumeSection volume={data.volume} />

          <div className="text-xs text-gray-400 space-y-0.5">
            <div>
              Window {data.window.startDate} – {data.window.endDate} ({data.window.days}{' '}
              days, UTC)
            </div>
            <div>
              Fetched {new Date(data.fetchedAt).toLocaleString()}
              {data.cached && ' (cached — Refresh forces a re-read)'}
            </div>
            {data.volume.available && (
              <div>Scanned {formatInt(data.volume.scanned)} audit-trail events</div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/** Configuration → OpenAI API tab. */
export function OpenAiUsageTab() {
  return (
    <div className="space-y-8">
      <OpenAiUsageSection />
    </div>
  );
}
